import { action, computed, observable } from 'mobx';
import { setter, toggle } from 'mobx-decorators';
import moment from 'moment-timezone';
import { deserializeWithMappers, setFieldValues } from 'stores/Utils';
import AccountStore from 'stores/Model/Account/AccountStore';
import AccountExtendStore from 'stores/Model/Account/AccountExtendStore';
import OrganizationStore from 'stores/Model/Organization/OrganizationStore';
import UserStore from 'stores/Model/User/UserStore';
import * as auth from 'Auth';
import constants from 'Constants';
import * as zendesk from 'Zendesk';
import * as comUtils from 'utils/Common';


class ConsoleStore {

  @setter @observable user;
  @setter @observable organization;
  @setter @observable account;
  @setter @observable accounts;
  @setter @observable permissions;

  constructor() {
    this.reset();
  }

  @action reset() {
    this.user = new UserStore();
    this.organization = new OrganizationStore();
    this.account = new AccountExtendStore();
    this.accounts = [];
    this.permissions = [];
  }

  getDeserializeMappers() {
    return [
      {"field": "user", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": UserStore},
      {"field": "organization", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": OrganizationStore},
      {"field": "account", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": AccountExtendStore},
      {"field": "accounts", "accessor": "accounts", "type": constants.DESERIALIZE_TYPE_ARRAY_OBJECTS, "cls": AccountStore},
      {"field": "permissions"},
    ];
  }

  @action deserialize(data) {
    deserializeWithMappers(this, this.getDeserializeMappers(), data);
  }

  @computed get billingDetails() {
    return this.account.billingDetails;
  }

  @computed get subscription() {
    return this.account.subscription;
  }

  @computed get defaultProduct() {
    return this.user.defaultProduct;
  }

  @computed get currentProduct() {
    return auth.getCurrentProduct();
  }

  @computed get isExceedingNumAccountLimits() {
    return this.accounts.length >= this.organization.numAccountsLimit;
  }

  @computed get canAddAccount() {
    if (this.isExceedingNumAccountLimits) return false;

    // uncomment if you want to disable adding account
    // if (!this.user.isStaff) return false;
    return true;
  }

  @computed get hasChangePermission() {
    if (this.organization.isSuspended) return false;
    if (!this.user.isVerified) return false;
    return this.permissions.includes(constants.PERMISSION_CHANGE);
  }

  @computed get hasViewPermission() {
    if (this.organization.isSuspended) return false;
    if (!this.user.isVerified) return false;
    return this.permissions.includes(constants.PERMISSION_VIEW);
  }

  @computed get showSettingsMenu() {
    if (!this.hasViewPermission) return false;
    return this.user.isOwner;
  }

  @computed get showBillingMenu() {
    if (!this.hasViewPermission) return false;
    return this.user.isOwner;
  }

  @computed get showControlMenu() {
    if (!this.hasViewPermission) return false;
    return this.user.isStaff;
  }

  @computed get showApiDetails() {
    if (!this.hasViewPermission) return false;
    return this.user.isOwner;
  }

  getTimezoneWithDefault(defaultTimezone=moment.tz.guess()) {
    const timezone = this.user.timezone;
    if (timezone) return timezone;
    return defaultTimezone;
  }

  getCurrency() {
    const currency = this.user.currency;
    if (currency) return currency;
    return;
  }
}


const consoleStore = new ConsoleStore();
export default consoleStore;
export { ConsoleStore };
